import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import LandingLayout from '../../components/Layouts/LandingLayout'
import { useAuthProvider } from '../../modules/Auth/providers/AuthSupabaseProvider/AuthProvider.hook'
import { brand, brandTitle } from '../../config/settings/brand'
import routes from '../../config/settings/routes'
import useDocumentSeo from '../../hooks/useDocumentSeo'
import {
  MarketingFooter,
  MarketingHeader,
} from '../Marketing/MarketingChrome'
import LandingProductPreview from './LandingProductPreview'
import {
  Hero,
  HeroCopy,
  HeroBrand,
  HeroHeadline,
  HeroSupport,
  CtaGroup,
  PrimaryCta,
  HeroVisual,
  Section,
  SectionNarrow,
  SectionTitle,
  SectionBody,
  Steps,
  Step,
  StepTitle,
  StepBody,
  ProofSection,
  FeatureList,
  FeatureItem,
  ProofVisual,
  TrustStrip,
  TrustItem,
  TrustItemTitle,
  TrustItemBody,
  TrustNote,
  FaqList,
  FaqItem,
  FaqQuestion,
  FaqAnswer,
  FinalCta,
  FinalTitle,
  FinalSupport,
  FinalCtaGroup,
} from './Landing.styles'

const loginPath = routes.login

const Landing = () => {
  useDocumentSeo({ title: brandTitle, path: routes.root })
  const { isAuthenticated, loading, initialLoading, logout } = useAuthProvider()
  const { profile, status: profileStatus } = useSelector(
    (state) => state.adminUserProfile
  )
  const navigate = useNavigate()
  const proofRef = useRef(null)
  const [proofVisible, setProofVisible] = useState(false)
  const [activeScene, setActiveScene] = useState('amount')

  const features = [
    {
      scene: 'amount',
      title: 'Monto estimado al día',
      body: 'Revalorización con UTM del SII o IMM vigente, sin recalcular a mano.',
    },
    {
      scene: 'history',
      title: 'Historial por período',
      body: 'Pendiente, pagado y evolución mes a mes en un solo lugar.',
    },
    {
      scene: 'config',
      title: 'Preferencias claras',
      body: 'Configura tu pensión según la resolución del tribunal (UTM o IMM).',
    },
  ]

  const faqs = [
    {
      question: `¿Es ${brand.name} asesoría legal?`,
      answer: brand.disclaimer,
    },
    {
      question: '¿Qué datos te pedimos?',
      answer:
        'Entras con Google. No pedimos RUT, banco, sueldo, datos de quien recibe ni la sentencia: solo el factor de tu resolución y el día de pago.',
    },
    {
      question: '¿Cómo se calcula el monto?',
      answer:
        'Según el método de tu resolución: UTM × cantidad, o un % del ingreso mínimo (IMM), con valores de referencia oficiales.',
    },
    {
      question: '¿Sirve si recibo la pensión alimenticia?',
      answer: `No está pensada para eso. ${brand.name} es para quien paga la pensión alimenticia y quiere cumplir sin improvisar el monto cada mes.`,
    },
    {
      question: '¿Cuesta algo?',
      answer:
        'El seguimiento principal es gratis hoy: ver el monto, el historial y los avisos, sin cobro al crear tu cuenta.',
    },
    {
      question: '¿Para qué sirve si ya uso Excel o miro el SII?',
      answer: `En Excel tienes que actualizar la UTM o el IMM a mano. En el SII solo ves el valor de referencia, no el monto de tu pensión. ${brand.name} aplica el factor de tu resolución a esos valores oficiales y te deja el monto del mes, el historial y los avisos en un solo lugar.`,
    },
  ]

  useEffect(() => {
    const authPending = loading || initialLoading
    const profilePending =
      isAuthenticated && profileStatus.fetch !== 'succeeded'
    const skippedThisSession =
      sessionStorage.getItem('onboarding_skipped') === 'true'
    const onboardingDone =
      profile?.onboardingStatus === 'completed' || skippedThisSession
    const willRedirectToDashboard =
      !authPending && !profilePending && isAuthenticated && onboardingDone

    // Authenticated users still finishing onboarding can view landing.
    // Only send completed/skipped-session users to the dashboard.
    if (willRedirectToDashboard) {
      navigate(routes.dashboard, { replace: true })
    }
  }, [
    isAuthenticated,
    loading,
    initialLoading,
    navigate,
    profile?.onboardingStatus,
    profileStatus.fetch,
  ])

  useEffect(() => {
    const node = proofRef.current
    if (!node || typeof IntersectionObserver === 'undefined') {
      setProofVisible(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setProofVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const handleLogin = async (event) => {
    event.preventDefault()
    if (isAuthenticated) {
      // Clear session so Google can ask which account to use.
      await logout()
      return
    }
    navigate(routes.login)
  }

  const header = <MarketingHeader onLoginClick={handleLogin} />
  const footer = <MarketingFooter onLoginClick={handleLogin} />

  return (
    <LandingLayout header={header} footer={footer}>
      <Hero>
        <HeroCopy>
          <HeroBrand>{brand.name}</HeroBrand>
          <HeroHeadline>Tu pensión alimenticia en Chile, al día.</HeroHeadline>
          <HeroSupport>
            Seguimiento de pensión alimenticia en Chile: monto estimado según tu
            resolución (UTM o IMM), historial claro y avisos a tiempo. Para pagar
            tranquilo, sin recalcular a mano.
          </HeroSupport>
          <CtaGroup>
            <PrimaryCta to={loginPath} onClick={handleLogin}>
              Continuar con Google
            </PrimaryCta>
          </CtaGroup>
        </HeroCopy>
        <HeroVisual>
          <LandingProductPreview interactive />
        </HeroVisual>
      </Hero>

      <TrustStrip>
        <TrustItem>
          <TrustItemTitle>Entras con Google</TrustItemTitle>
          <TrustItemBody>
            Sin contraseña nueva. Usamos tu cuenta de Google para identificarte.
          </TrustItemBody>
        </TrustItem>
        <TrustItem>
          <TrustItemTitle>No pedimos RUT ni banco</TrustItemTitle>
          <TrustItemBody>
            Tampoco sueldo, datos de quien recibe ni la sentencia. Solo el
            factor de tu resolución y el día de pago.
          </TrustItemBody>
        </TrustItem>
        <TrustItem>
          <TrustItemTitle>UTM e IMM oficiales</TrustItemTitle>
          <TrustItemBody>
            El monto estimado usa valores de referencia públicos, según el
            método de tu resolución.
          </TrustItemBody>
        </TrustItem>
      </TrustStrip>

      <SectionNarrow>
        <SectionTitle>
          Cuando cambian los valores de referencia, tu monto también. Aquí lo ves
          venir.
        </SectionTitle>
        <SectionBody>
          Si pagas pensión alimenticia, un reajuste no es un detalle: es el monto
          que corresponde este mes. En vez de buscarlo en una planilla o en el
          correo del mes pasado, {brand.name} te muestra la estimación según tu
          método — UTM o % del sueldo mínimo (IMM) —, clara, a la vista, lista
          para pagar.
        </SectionBody>
      </SectionNarrow>

      <Section>
        <SectionTitle>Una sola vista. Todo lo que necesitas saber.</SectionTitle>
        <SectionBody>
          Tres pasos. Sin planillas compartidas ni cálculo a mano el día del abono.
        </SectionBody>
        <Steps>
          <Step>
            <StepTitle>Ingresa con Google</StepTitle>
            <StepBody>
              Crea tu cuenta en segundos y entra a un panel seguro para tu pensión.
            </StepBody>
          </Step>
          <Step>
            <StepTitle>Configura tu resolución</StepTitle>
            <StepBody>
              Registra lo que fijó el Juzgado de Familia — en UTM o como % del
              ingreso mínimo (IMM) — y alinea el cálculo a tu modalidad.
            </StepBody>
          </Step>
          <Step>
            <StepTitle>Sigue monto, historial y avisos</StepTitle>
            <StepBody>
              Revisa la estimación del mes, el historial por período y mantente al
              tanto de cambios.
            </StepBody>
          </Step>
        </Steps>
      </Section>

      <ProofSection>
        <div>
          <SectionTitle>Claridad, no intuición.</SectionTitle>
          <SectionBody>
            Todo lo que necesitas para saber cuánto corresponde este mes.
          </SectionBody>
          <FeatureList>
            {features.map((feature) => (
              <FeatureItem
                key={feature.scene}
                type="button"
                $active={activeScene === feature.scene}
                onClick={() => setActiveScene(feature.scene)}
                onMouseEnter={() => setActiveScene(feature.scene)}
              >
                <strong>{feature.title}</strong>
                {feature.body}
              </FeatureItem>
            ))}
          </FeatureList>
        </div>
        <ProofVisual ref={proofRef} data-visible={proofVisible}>
          <LandingProductPreview scene={activeScene} />
        </ProofVisual>
      </ProofSection>

      <SectionNarrow>
        <SectionTitle>Pensado para pensiones alimenticias en Chile.</SectionTitle>
        <SectionBody>
          {brand.name} está pensada para pensiones alimenticias fijadas por el
          Juzgado de Familia, con seguimiento según tu resolución — en UTM
          (Ley N° 21.389) o por % del ingreso mínimo / IMM (Ley N° 21.830) — y
          valores de referencia oficiales. Tú decides; la plataforma ordena la
          información.
        </SectionBody>
        <TrustNote>
          {brand.disclaimer}
        </TrustNote>
      </SectionNarrow>

      <SectionNarrow>
        <SectionTitle>Preguntas frecuentes</SectionTitle>
        <SectionBody>
          Lo esencial antes de entrar: alcance, datos y cómo se estima el monto.
        </SectionBody>
        <FaqList>
          {faqs.map((faq) => (
            <FaqItem key={faq.question}>
              <FaqQuestion>{faq.question}</FaqQuestion>
              <FaqAnswer>{faq.answer}</FaqAnswer>
            </FaqItem>
          ))}
        </FaqList>
      </SectionNarrow>

      <FinalCta>
        <FinalTitle>Entra y deja tu pensión alimenticia en orden.</FinalTitle>
        <FinalSupport>
          Abre tu cuenta, configura tu pensión alimenticia y ten el monto claro
          cuando llegue el día del pago.
        </FinalSupport>
        <FinalCtaGroup>
          <PrimaryCta to={loginPath} onClick={handleLogin}>
            Continuar con Google
          </PrimaryCta>
        </FinalCtaGroup>
      </FinalCta>
    </LandingLayout>
  )
}

export default Landing
